import { IconButtonOutgoingLink, OutgoingLink } from '@47ng/chakra-next'
import {
  Avatar,
  Box,
  BoxProps,
  Divider,
  Flex,
  Grid,
  Heading,
  Icon,
  Text,
  useColorModeValue
} from '@chakra-ui/react'
import { DimmedText } from 'modules/ui/typography/dimmedText'
import React from 'react'
import { IconType } from 'react-icons'
import {
  FiClock,
  FiDollarSign,
  FiExternalLink,
  FiFile,
  FiGlobe,
  FiHelpCircle,
  FiLink,
  FiLock,
  FiToggleLeft,
  FiUser
} from 'react-icons/fi'

export interface ThirdPartyServicePrivacyCardData {
  name: string
  website: string
  image: string
  what?: React.ReactNode
  when?: React.ReactNode
  why?: React.ReactNode
  how?: React.ReactNode
  who?: React.ReactNode
  where?: React.ReactNode
  optOut?: React.ReactNode
  privacyPolicyURL?: string
  fundingModel?: string
}

export interface ThirdPartyServicePrivacyCardProps
  extends BoxProps,
    ThirdPartyServicePrivacyCardData {}

export const ThirdPartyServicePrivacyCard: React.FC<
  ThirdPartyServicePrivacyCardProps
> = ({
  name,
  website,
  image,
  what,
  when,
  why,
  how,
  who,
  optOut,
  where,
  privacyPolicyURL,
  fundingModel,
  children,
  ...props
}) => {
  return (
    <Box
      as="li"
      rounded="md"
      shadow="md"
      bg={useColorModeValue('white', 'gray.800')}
      overflow="hidden"
      {...props}
    >
      <Box as="header" px={8} py={6}>
        <Flex alignItems="center" mb={4}>
          {image && (
            <Avatar size="sm" src={image} name={name} bg="none" mr={3} />
          )}
          <Heading fontSize="2xl">{name}</Heading>
          <IconButtonOutgoingLink
            href={website}
            aria-label={`${name}'s website`}
            icon={<FiExternalLink />}
            variant="ghost"
            rounded="full"
            ml="auto"
            mr={-2}
            size="sm"
            color={useColorModeValue('gray.500', 'gray.400')}
          />
        </Flex>
        {children}
      </Box>
      <Divider />
      <Grid
        px={8}
        py={6}
        gridTemplateColumns={{ base: '1fr', md: '12rem 1fr' }}
        gridGap={{ base: 0, md: 2 }}
        {...props}
      >
        {!!what && (
          <Privacy3rdPartyDetails
            icon={FiFile}
            question="What"
            detail=" is sent to them"
          >
            {what}
          </Privacy3rdPartyDetails>
        )}
        {!!when && (
          <Privacy3rdPartyDetails
            icon={FiClock}
            question="When"
            detail=" it is sent"
          >
            {when}
          </Privacy3rdPartyDetails>
        )}
        {!!why && (
          <Privacy3rdPartyDetails
            icon={FiHelpCircle}
            question="Why"
            detail=", for what purpose"
          >
            {why}
          </Privacy3rdPartyDetails>
        )}
        {!!how && (
          <Privacy3rdPartyDetails
            icon={FiLock}
            question="How"
            detail=" it is transmitted"
          >
            {how}
          </Privacy3rdPartyDetails>
        )}
        {!!who && (
          <Privacy3rdPartyDetails
            icon={FiUser}
            question="Who"
            detail=" it is sent to"
          >
            {who}
          </Privacy3rdPartyDetails>
        )}
        {!!where && (
          <Privacy3rdPartyDetails
            icon={FiGlobe}
            question="Where"
            detail=" it is stored"
          >
            {where}
          </Privacy3rdPartyDetails>
        )}
        {!!optOut && (
          <Privacy3rdPartyDetails
            icon={FiToggleLeft}
            question="Opt-out"
            detail=" is possible"
          >
            {optOut}
          </Privacy3rdPartyDetails>
        )}
      </Grid>
      <Divider />
      <Flex
        as="footer"
        px={8}
        py={4}
        // bg={useColorModeValue('gray.50', 'gray.900')}
        flexDirection={{ base: 'column', md: 'row' }}
      >
        {fundingModel && (
          <DimmedText>
            <Icon as={FiDollarSign} verticalAlign="sub" mr={2} />
            Funding model: <em>{fundingModel}</em>
          </DimmedText>
        )}
        {privacyPolicyURL && (
          <OutgoingLink
            href={privacyPolicyURL}
            fontSize="sm"
            color="gray.500"
            ml={{ base: 0, md: 'auto' }}
            mt={{ base: fundingModel ? 2 : 0, md: 0 }}
          >
            <Icon as={FiLink} verticalAlign="sub" mr={2} />
            {name}'s privacy policy
          </OutgoingLink>
        )}
      </Flex>
    </Box>
  )
}

// --

export interface Privacy3rdPartyDetailsProps extends BoxProps {
  icon: IconType
  question: string
  detail: string
}

export const Privacy3rdPartyDetails: React.FC<Privacy3rdPartyDetailsProps> = ({
  question,
  detail,
  icon,
  children,
  ...props
}) => {
  return (
    <>
      <Box mb={{ base: 1, md: 0 }}>
        <Flex {...props} alignItems="center" fontSize="sm">
          <Icon as={icon} mr={2} color="gray.500" />
          <Text as="span">
            <Text as="b" fontWeight="medium">
              {question}
            </Text>
            <DimmedText as="small">{detail}</DimmedText>
          </Text>
        </Flex>
      </Box>
      <Box fontSize="sm" mb={{ base: 4, md: 0 }}>
        {children}
      </Box>
    </>
  )
}
